import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Check, Info, MapPin } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

import { CATEGORIES } from "@/lib/constants";
import { requestLocation, type Coords } from "@/lib/location";
import { reverseGeocode, dialCode, phoneMatchesCountry, type PlaceInfo } from "@/lib/geo";
import { pageHead } from "@/lib/seo";

export const Route = createFileRoute("/register")({
  head: () => pageHead({
    path: "/register",
    title: "Create your free account — Book Swap",
    description: "Join Book Swap in a minute: list the books on your shelf and start borrowing, swapping and gifting with neighbors near you.",
  }),
  validateSearch: (s: Record<string, unknown>): { ref?: string } => ({ ref: typeof s.ref === "string" ? s.ref : undefined }),
  component: Register,
});

function Register() {
  const navigate = useNavigate();
  const { ref } = Route.useSearch();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [coords, setCoords] = useState<Coords | null>(null);
  const [place, setPlace] = useState<PlaceInfo | null>(null);
  const [locating, setLocating] = useState(false);
  const [interests, setInterests] = useState<string[]>([]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState("");
  const [resending, setResending] = useState(false);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const pwValid = password.length >= 8;
  const confirmValid = confirm.length > 0 && confirm === password;
  const phoneValid = phoneMatchesCountry(phone, place?.countryCode ?? null);

  function toggleInterest(c: string) {
    setInterests((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);
  }

  async function captureLocation() {
    setLocating(true);
    try {
      const c = await requestLocation();
      setCoords(c);
      try {
        const p = await reverseGeocode(c.lat, c.lng);
        if (!p) throw new Error("Place lookup unavailable");
        setPlace(p);
        setAddress(p.formatted);
        const code = dialCode(p.countryCode);
        if (code && !phone.trim().startsWith("+")) setPhone(code + " ");
        toast.success(`Location confirmed: ${p.formatted}`);
      } catch {
        setPlace(null);
        toast.success("Location captured. Please enter your city and international mobile number.");
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Could not get your location. Please allow location access.");
    } finally {
      setLocating(false);
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return toast.error("Full name is required.");
    if (!coords) return toast.error("Please share your location — it's required to find books near you.");
    if (!phone.trim()) return toast.error("Mobile number is required.");
    if (!phoneValid) return toast.error(place
      ? `Enter a valid ${place.country} mobile number (starting with ${dialCode(place.countryCode)}).`
      : "Enter a valid international mobile number starting with + and its country code.");
    if (!address.trim()) return toast.error("Short address is required.");
    if (!emailValid) return toast.error("Please enter a valid email address.");

    if (!pwValid) return toast.error("Password must be at least 8 characters.");
    if (!confirmValid) return toast.error("Passwords do not match.");
    if (!agree) return toast.error("Please agree to the Terms of Use and Privacy Policy.");

    setLoading(true);
    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: {
        emailRedirectTo: `${window.location.origin}/browse`,
        data: {
          full_name: name,
          phone_number: phone,
          address,
          lat: String(coords.lat),
          lng: String(coords.lng),
          interested_categories: interests,
        },
      },
    });
    if (error) {
      setLoading(false);
      console.error(error);
      const msg = error.message || "";
      const code = (error as any).code || "";
      const friendly =
        code === "weak_password" || /pwned|weak.?password|known to be weak/i.test(msg)
          ? "This password has been found in a data breach. Please choose a stronger, unique password (try mixing upper/lowercase letters, numbers, and symbols)."
        : /already|registered|exists/i.test(msg) ? "An account with this email already exists. Try signing in instead."
        : /password/i.test(msg) ? msg
        : /email/i.test(msg) ? msg
        : `Could not create your account: ${msg}`;
      return toast.error(friendly);
    }
    if (data.user && data.user.identities && data.user.identities.length === 0) {
      // Supabase returns a fake user with empty identities when the email is already registered
      setLoading(false);
      return toast.error("An account with this email already exists. Try signing in instead.");
    }
    if (data.user) {
      setLoading(false);
      setOtpStep(true);
      toast.success("We sent a 6-digit code to your email.");
      return;
    }
    setLoading(false);
  }

  async function onVerifyOtp(e: React.FormEvent) {
    e.preventDefault();
    if (otp.trim().length < 6) return toast.error("Enter the 6-digit code from your email.");
    setLoading(true);
    const { data, error } = await supabase.auth.verifyOtp({
      email: email.trim().toLowerCase(),
      token: otp.trim(),
      type: "signup",
    });
    if (error || !data.session || !data.user) {
      setLoading(false);
      return toast.error(error?.message || "Invalid or expired code. Please try again.");
    }
    // Session is live now — safe to write profile top-up and referral
    await supabase.from("profiles").update({
      full_name: name,
      phone_number: phone,
      address,
      lat: coords!.lat,
      lng: coords!.lng,
      interested_categories: interests,
    }).eq("id", data.user.id);

    if (ref) {
      const { data: rc } = await supabase.from("referral_codes").select("user_id").eq("code", ref).maybeSingle();
      if (rc) await supabase.from("referrals").insert({ inviter_id: rc.user_id, invitee_id: data.user.id, code: ref });
    }
    setLoading(false);
    toast.success("Email verified! Welcome to Book Swap.");
    navigate({ to: "/browse" });
  }

  async function onResendOtp() {
    setResending(true);
    const { error } = await supabase.auth.resend({ type: "signup", email: email.trim().toLowerCase() });
    setResending(false);
    if (error) return toast.error(error.message || "Could not resend code.");
    toast.success("New code sent — check your inbox.");
  }


  if (otpStep) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="mx-auto max-w-md px-4 py-16">
          <div className="rounded-2xl bg-card p-8 shadow-[var(--shadow-card)]">
            <h1 className="text-2xl font-bold">Verify your email</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              We sent a 6-digit code to <span className="font-medium text-foreground">{email}</span>. Enter it below to activate your account.
            </p>
            <form onSubmit={onVerifyOtp} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="otp">Verification code</Label>
                <Input
                  id="otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  className="text-center text-lg tracking-[0.5em]"
                  required
                />
              </div>
              <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={loading || otp.length < 6}>
                {loading ? "Verifying…" : "Verify & continue"}
              </Button>
            </form>
            <div className="mt-4 flex items-center justify-between text-sm">
              <button type="button" onClick={onResendOtp} disabled={resending} className="font-medium text-primary hover:underline disabled:opacity-50">
                {resending ? "Sending…" : "Resend code"}
              </button>
              <button type="button" onClick={() => setOtpStep(false)} className="text-muted-foreground hover:underline">
                Use a different email
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-md px-4 py-16">
        <div className="rounded-2xl bg-card p-8 shadow-[var(--shadow-card)]">
          <h1 className="text-2xl font-bold">Join Book Swap</h1>
          <p className="mt-1 text-sm text-muted-foreground">Free forever. Books just down the street.</p>
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="name">Full name *</Label>
              <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <div className="relative">
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={email && !emailValid ? "border-destructive pr-9" : email ? "pr-9" : ""} />
                {email && emailValid && <Check className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-green-600" />}
              </div>
              {email && !emailValid && <p className="mt-1 text-xs text-destructive">Please enter a valid email address.</p>}
            </div>
            <div>
              <Label>Location *</Label>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <Button type="button" variant={coords ? "default" : "outline"} size="sm" onClick={captureLocation} disabled={locating}>
                  <MapPin className="mr-1 h-4 w-4" />
                  {locating ? "Verifying location…" : coords ? "Location captured ✓" : "Use my current location"}
                </Button>
                {place && <span className="text-xs text-muted-foreground">{place.formatted}</span>}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Required to find nearby books. We fill your city and phone code when place lookup is available.
              </p>
            </div>
            <div>
              <Label htmlFor="phone">Mobile number *</Label>
              <Input
                id="phone"
                type="tel"
                required
                disabled={!coords}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={place ? `${dialCode(place.countryCode)} …` : coords ? "+966 …" : "Share your location first"}
                className={phone && !phoneValid ? "border-destructive" : ""}
              />
              <p className={`mt-1 text-xs ${phone && !phoneValid ? "text-destructive" : "text-muted-foreground"}`}>
                {place
                  ? `Must be a valid ${place.country} number starting with ${dialCode(place.countryCode)}.`
                  : coords ? "Enter the full number starting with + and its country code." : "Available once your location is captured."}
              </p>
            </div>
            <div>
              <Label htmlFor="address" className="flex items-center gap-1.5">
                City & country *
                <span title="We only show your neighborhood to other readers — never your exact address." className="inline-flex">
                  <Info className="h-3.5 w-3.5 text-muted-foreground" aria-label="Privacy note" />
                </span>
              </Label>
              <Input id="address" required readOnly={!!place} value={address} onChange={(e) => setAddress(e.target.value)} placeholder={place ? "Filled automatically from your location" : "Enter your city and country"} />
              <p className="mt-1 text-xs text-muted-foreground">We never show your exact address.</p>
            </div>

            <div>
              <Label>Interested in which type of books?</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {CATEGORIES.map((c) => (
                  <label key={c} className="flex items-center gap-2 rounded-md border border-border p-2 text-sm">
                    <Checkbox checked={interests.includes(c)} onCheckedChange={() => toggleInterest(c)} />
                    <span>{c}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="password">Password *</Label>
              <div className="relative">
                <Input id="password" type="password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} className={password && !pwValid ? "border-destructive pr-9" : password ? "pr-9" : ""} />
                {password && pwValid && <Check className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-green-600" />}
              </div>
              <p className={`mt-1 text-xs ${password && !pwValid ? "text-destructive" : "text-muted-foreground"}`}>At least 8 characters. Avoid common passwords — they're checked against known breaches.</p>
            </div>
            <div>
              <Label htmlFor="confirm">Confirm password *</Label>
              <div className="relative">
                <Input id="confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className={confirm && !confirmValid ? "border-destructive pr-9" : confirm ? "pr-9" : ""} />
                {confirm && confirmValid && <Check className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-green-600" />}
              </div>
              {confirm && !confirmValid && <p className="mt-1 text-xs text-destructive">Passwords do not match.</p>}
            </div>
            <label className="flex items-start gap-2 rounded-md bg-primary-soft/40 p-3 text-sm">
              <Checkbox checked={agree} onCheckedChange={(v) => setAgree(v === true)} className="mt-0.5" />
              <span className="text-muted-foreground">
                I agree to the{" "}
                <Link to="/terms" className="font-medium text-primary hover:underline">Terms of Use</Link>
                {" "}and{" "}
                <Link to="/privacy" className="font-medium text-primary hover:underline">Privacy Policy</Link>.
              </span>
            </label>
            <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={loading || !agree}>
              {loading ? "Creating account…" : "Create account"}
            </Button>
          </form>
          <p className="mt-4 text-center text-sm text-muted-foreground">
            Already a member? <Link to="/login" className="font-medium text-primary hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
